// frontend/src/main.js
import { createApp } from 'vue';
import App from './App.vue';

// ---------- Helper: формат чисел ----------
export function formatDisplay(n) {
  if (typeof n !== 'number') n = Number(n) || 0;
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// ---------- Telegram init helpers ----------
function getTelegramInitUser() {
  try {
    if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe) {
      const u = window.Telegram.WebApp.initDataUnsafe.user;
      if (u && u.id) {
        return {
          id: String(u.id),
          first_name: u.first_name || '',
          username: u.username || ''
        };
      }
    }
  } catch (e) {
    console.warn('Telegram parse error', e);
  }
  // Return null if Telegram WebApp is not available or user data not found
  return null;
}

// ---------- Game API ----------
const gameApi = {
  userId: null,
  state: {
    balance: 0,
    energyCurrent: 1000,
    energyMax: 1000,
    clickPower: 1,
    autoTapLevel: 0,
    autoTapIntervalMs: 15000,
    boostActive: false,
    boostExpireAt: null,
    lastBoostActivation: null,
    tasksCompleted: {},
    referrals: [],
    totalClicks: 0,
    lastAutoTapSummary: { tapsSinceLastVisit: 0, earnedSinceLastVisit: 0 }
  },

  saveTimeout: null,

  // init user
  async init() {
    const tgUser = getTelegramInitUser();

    if (!tgUser) {
      const fallback = localStorage.getItem('astro_local_user');
      if (fallback) {
        this.userId = fallback;
      } else {
        this.userId = 'anon_' + Date.now();
        localStorage.setItem('astro_local_user', this.userId);
      }
      return;
    }

    this.userId = String(tgUser.id);
    // We'll load the balance in the Vue app component instead
  },

  // Debounced save
  debounceSave(delay = 2000) {
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(async () => {
      await this.saveUserData();
    }, delay);
  },

  // Save user data to localStorage (for non-balance data)
  async saveUserData() {
    if (!this.userId) return;

    // Save other game state to localStorage
    try {
      localStorage.setItem('astro_state_' + this.userId, JSON.stringify({
        energyCurrent: this.state.energyCurrent,
        energyMax: this.state.energyMax,
        clickPower: this.state.clickPower,
        autoTapLevel: this.state.autoTapLevel,
        boostActive: this.state.boostActive,
        boostExpireAt: this.state.boostExpireAt
      }));
    } catch (e) {
      console.error("❌ Error saving game state to localStorage:", e);
    }
  },

  // game actions
  async onTap() {
    if (this.state.energyCurrent <= 0) return { ok: false, reason: 'no-energy' };
    
    //_gain = уровень клика (1, 2, 3... монет за клик)
    // Во время буста удваиваем доход
    const baseGain = this.state.clickPower;
    const gain = this.state.boostActive ? baseGain * 2 : baseGain;
    
    this.state.energyCurrent -= this.state.clickPower; // тратим энергию равную силе клика
    this.state.totalClicks = (this.state.totalClicks || 0) + 1;
    
    // Сохраняем общее количество кликов
    try {
      localStorage.setItem('astro_total_clicks', this.state.totalClicks.toString());
    } catch (e) {
      console.error('Error saving click count:', e);
    }
    
    this.debounceSave();
    window.dispatchEvent(new CustomEvent('astro:user-updated', { detail: { state: this.state } }));
    return { ok: true, gain };
  },

  async buyClickUpgrade(price) {
    if (this.state.balance < price) return { ok: false, reason: 'no-money' };
    this.state.balance -= price;
    this.state.clickPower += 1;
    
    // Каждое улучшение дает +1 клик за тап
    // Уровень 1: 1 монета за клик
    // Уровень 2: 2 монеты за клик
    // Уровень 3: 3 монеты за клик и т.д.
    
    this.debounceSave();
    window.dispatchEvent(new CustomEvent('astro:user-updated', { detail: { state: this.state } }));
    window.dispatchEvent(new CustomEvent('astro:upgrade', { detail: { type: 'click', level: this.state.clickPower } }));
    return { ok: true };
  },

  async buyEnergyUpgrade(price) {
    if (this.state.balance < price) return { ok: false, reason: 'no-money' };
    this.state.balance -= price;
    this.state.energyMax += 500;
    this.state.energyCurrent = Math.min(this.state.energyCurrent + 500, this.state.energyMax);
    this.debounceSave();
    window.dispatchEvent(new CustomEvent('astro:user-updated', { detail: { state: this.state } }));
    window.dispatchEvent(new CustomEvent('astro:upgrade', { detail: { type: 'energy', level: this.state.energyMax } }));
    return { ok: true };
  },

  async buyAutoTapUpgrade(price) {
    if (this.state.balance < price) return { ok: false, reason: 'no-money' };
    this.state.balance -= price;
    this.state.autoTapLevel += 1;
    
    // Авто-тап: 1 клик раз в N секунд
    // Начальный интервал: 20 секунд
    // Каждое улучшение: -1 секунда
    // Минимальный интервал: 1 секунда
    const baseInterval = 20000; // 20 секунд
    this.state.autoTapIntervalMs = Math.max(1000, baseInterval - ((this.state.autoTapLevel - 1) * 1000));
    
    this.debounceSave();
    window.dispatchEvent(new CustomEvent('astro:user-updated', { detail: { state: this.state } }));
    window.dispatchEvent(new CustomEvent('astro:upgrade', { detail: { type: 'autotap', level: this.state.autoTapLevel } }));
    return { ok: true };
  },

  async activateBoost() {
    if (!this.userId) return { ok: false };
    
    // Проверяем кулдаун 12 часов
    const now = Date.now();
    const lastBoostTime = this.state.lastBoostActivation || 0;
    const cooldownPeriod = 12 * 60 * 60 * 1000; // 12 часов
    
    if (now - lastBoostTime < cooldownPeriod) {
      const remainingTime = cooldownPeriod - (now - lastBoostTime);
      return { ok: false, reason: 'cooldown', remainingTime };
    }
    
    // Boost бесплатный
    const expireAt = Date.now() + 5 * 60 * 1000; // 5 минут
    this.state.boostActive = true;
    this.state.boostExpireAt = expireAt;
    this.state.lastBoostActivation = now; // Сохраняем время активации
    this.debounceSave();
    
    // Показываем уведомление об активации
    window.showNotification?.('🚀 Буст активирован!', 'Теперь каждый клик дает x2 монет на 5 минут', 'success');
    
    // Планируем уведомление о следующей активации
    setTimeout(() => {
      const nextActivationTime = new Date(now + cooldownPeriod);
      const hours = Math.floor(cooldownPeriod / (1000 * 60 * 60));
      window.showNotification?.('⏰ Следующая активация', `Через ${hours} часов (${nextActivationTime.toLocaleTimeString()})`, 'info');
    }, 100);

    setTimeout(() => {
      this.state.boostActive = false;
      this.state.boostExpireAt = null;
      this.debounceSave();
      window.dispatchEvent(new CustomEvent('astro:boost-ended'));
    }, 5 * 60 * 1000);

    window.dispatchEvent(new CustomEvent('astro:boost-started', { detail: { expireAt } }));
    return { ok: true, expireAt };
  },

  getReferralLink() {
    const botUsername = 'AstroClickerBot';
    const refCode = this.userId || 'unknown';
    return `https://t.me/${botUsername}?start=${encodeURIComponent(refCode)}`;
  }
};

// Auto init
(async () => {
  await gameApi.init();
})();

window.AstroGameApi = gameApi;

// Initialize Telegram WebApp ready
if (window.Telegram?.WebApp) {
  window.Telegram.WebApp.ready();
} else {
  console.warn('Telegram WebApp not available. Running in development mode.');
}

// Mount Vue app
createApp(App).mount('#app');

export default gameApi;